import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { eventsService } from '../../services/eventsService.js';
import styles from './EventAttendees.module.css';

function paymentLabel(status) {
  return { free: 'Free', paid: 'Paid', refunded: 'Refunded' }[status] || status;
}

function CapacityMeter({ registered, capacity }) {
  const safeCapacity = Math.max(1, capacity);
  const pct = Math.min(100, Math.round((registered / safeCapacity) * 100));
  const full = registered >= capacity;
  let tone = 'ok';
  if (full) tone = 'full';
  else if (pct >= 95) tone = 'red';
  else if (pct >= 80) tone = 'amber';
  return (
    <div className={styles.capacityWrap}>
      <span className={styles.capacityLabel}>
        {full ? 'Sold out' : `${registered} / ${capacity} registered`}
      </span>
      <span className={styles.capacityBar} aria-hidden="true">
        <span
          className={`${styles.capacityFill} ${styles[`capacityFill_${tone}`]}`}
          style={{ width: `${pct}%` }}
        />
      </span>
    </div>
  );
}

export default function EventAttendees() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [ev, list] = await Promise.all([
          eventsService.getEvent(id),
          eventsService.getAttendees(id),
        ]);
        if (!active) return;
        if (!ev) { toast.error('Event not found'); navigate('/events'); return; }
        setEvent(ev);
        setAttendees(list);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, navigate]);

  async function handleCsv() {
    setDownloading(true);
    try {
      const blob = await eventsService.downloadAttendeesCsv(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendees-${id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.loadingWrap}>
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.loadingText}>Loading attendees…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Link to="/events" className={styles.back}>
          <ArrowLeft size={14} />
          Events
        </Link>

        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Attendees</h1>
            {event && <p className={styles.subtitle}>{event.title}</p>}
            {event && <CapacityMeter registered={event.registered_count ?? attendees.length} capacity={event.capacity} />}
          </div>
          <button type="button" className={styles.csvBtn} onClick={handleCsv} disabled={downloading || attendees.length === 0}>
            <Download size={14} />
            {downloading ? 'Downloading…' : 'Download CSV'}
          </button>
        </div>

        {error && (
          <div className={styles.errorBox} role="alert">
            <AlertCircle size={16} className={styles.errorIcon} />
            <p className={styles.errorText}>{error}</p>
          </div>
        )}

        {!error && attendees.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyTitle}>No attendees yet</p>
            <p className={styles.emptyBody}>Attendees will appear here once the event is published and they register.</p>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Name</th>
                  <th className={styles.th}>Email</th>
                  <th className={styles.th}>Payment</th>
                  <th className={styles.th}>Registered</th>
                </tr>
              </thead>
              <tbody>
                {attendees.map((a) => (
                  <tr key={a.id} className={styles.row}>
                    <td className={styles.td}>{a.name}</td>
                    <td className={styles.td}>{a.email}</td>
                    <td className={styles.td}>
                      <span className={`${styles.badge} ${styles[`badge_${a.payment_status}`] || ''}`}>
                        {paymentLabel(a.payment_status)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      {new Date(a.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
